import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    CircularProgress,
    Box,
    Typography,
    Alert,
    Paper
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { FinanceService } from '../../../api/services/financeService';
import { toast } from '../../../utils/toast';
import { useCurrency, formatCurrency } from '../../../utils/currency';

export default function VoidFolioChargeModal({ open, onClose, folioId: drawerFolioId, charge, onSuccess }) {
    const { currency } = useCurrency();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [voidReason, setVoidReason] = useState('');

    useEffect(() => {
        if (open) {
            setVoidReason('');
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!voidReason.trim()) {
            toast.error('A void reason is required to process this audit adjustment.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                voidReason: voidReason.trim()
            };

            const chargeId = charge.id || charge.chargeId || charge._id;
            const folioId = drawerFolioId || charge.folioId;
            await FinanceService.voidFolioCharge(folioId, chargeId, payload);
            toast.success('Folio charge successfully voided; billing metrics recalculated');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to void manual charge:', error);
            // Handled via global interceptor
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={!isSubmitting ? onClose : undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#fff5f5', color: 'error.main', display: 'flex', alignItems: 'center' }}>
                <DeleteForeverIcon sx={{ mr: 1 }} /> Void Folio Charge
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
                <Box sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You are about to permanently void this financial transaction line-item. This action will deduct its value from the guest's folio record and cannot be undone.
                    </Alert>

                    {charge && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(0,0,0,0.02)', borderColor: '#eee' }}>
                            <Typography variant="body2" fontWeight="bold">Target Charge Details</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                <b>Description:</b> {charge.description || charge.category || 'N/A'}<br/>
                                <b>Amount:</b> {formatCurrency(Math.abs(charge.amount || charge.value || 0), currency)}
                            </Typography>
                        </Paper>
                    )}

                    <TextField
                        fullWidth
                        required
                        label="Void Reason (Required Audit Explanation)"
                        placeholder="e.g., Guest was overcharged, Incorrect posting category..."
                        value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                        multiline
                        rows={3}
                        disabled={isSubmitting}
                        error={voidReason.trim() === '' && isSubmitting}
                        helperText={voidReason.trim() === '' && isSubmitting ? "Audit reason is strictly required" : ""}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', bgcolor: '#fbfbfb' }}>
                <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="error"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ px: 3 }}
                >
                    {isSubmitting ? 'Processing Void...' : 'Confirm Void Action'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
